/*
 * HTTP client for the real Smart Travel backend (API_CONTRACT.md, base /api/v1).
 *
 * The app talks to the API layer (trips.ts, points.ts, auth.ts, today.ts, …),
 * each of which dispatches to either the mock or this client based on
 * USE_MOCK_API. Swapping does not touch React Query hooks or UI.
 */
import { ApiError } from './store'

/** When true, the API layer uses the in-memory mock. Defaults to true so the
 *  app runs with no backend; set VITE_USE_MOCK_API=0 to hit the real API. */
export const USE_MOCK_API: boolean =
  (import.meta.env.VITE_USE_MOCK_API ?? '1') !== '0'

export const API_BASE_URL: string =
  import.meta.env.VITE_API_URL ?? 'http://localhost:8093/api/v1'

const TOKEN_KEY = 'smart-travel:token'

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    const previous = localStorage.getItem(TOKEN_KEY)
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
    if (previous !== token && typeof caches !== 'undefined') void caches.delete('st-api-read')
  } catch {
    /* ignore storage failures */
  }
}

function authHeaders(extra?: HeadersInit): HeadersInit {
  const token = getToken()
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  }
}

async function parseError(res: Response): Promise<never> {
  let message = res.statusText
  try {
    const body = await res.json()
    message = body?.error?.message ?? message
  } catch {
    /* non-JSON error body */
  }
  throw new ApiError(res.status, message)
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: authHeaders() })
  if (!res.ok) return parseError(res)
  return res.json() as Promise<T>
}

export async function apiGetBlob(path: string): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: authHeaders() })
  if (!res.ok) return parseError(res)
  return res.blob()
}

export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) return parseError(res)
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export async function apiDelete(path: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  if (!res.ok) return parseError(res)
}

export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  })
  if (!res.ok) return parseError(res)
  return res.json() as Promise<T>
}

/** POST /auth/google — reads the issued JWT from the x-auth-token header. */
export async function apiGoogleLogin(idToken: string): Promise<{ user: unknown; token: string | null }> {
  const res = await fetch(`${API_BASE_URL}/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  })
  if (!res.ok) return parseError(res)
  const token = res.headers.get('x-auth-token')
  const user = await res.json()
  return { user, token }
}
